import { parseMultiSelectPlainText } from '../property-value.js';
import type { DatabaseColumnType } from '../schema.js';
import { DATABASE_COLUMN_TYPES } from '../schema.js';
import { parseDateInput } from '../date-format.js';
import { parseImportDateRange, parseImportNumber } from './parse-import-values.js';

const IMPORTABLE_TYPES = new Set<string>(
  DATABASE_COLUMN_TYPES.filter((t) => t !== 'relation' && t !== 'rollup'),
);

const CHECKBOX_VALUES = new Set([
  'true',
  'false',
  'yes',
  'no',
  '0',
  '1',
  'y',
  'n',
  'checked',
  'unchecked',
]);

const URL_PATTERN = /^(https?:\/\/|www\.)/i;

function nonEmpty(values: string[]): string[] {
  return values.map((v) => v.trim()).filter((v) => v.length > 0);
}

function allNumbers(values: string[]): boolean {
  if (values.length === 0) {
    return false;
  }
  return values.every((v) => parseImportNumber(v) !== null);
}

function allCheckboxes(values: string[]): boolean {
  if (values.length === 0) {
    return false;
  }
  return values.every((v) => CHECKBOX_VALUES.has(v.toLowerCase()));
}

function allUrls(values: string[]): boolean {
  if (values.length === 0) {
    return false;
  }
  return values.every((v) => URL_PATTERN.test(v.trim()));
}

function allDates(values: string[]): boolean {
  if (values.length === 0) {
    return false;
  }
  return values.every((v) => {
    const range = parseImportDateRange(v);
    if (range) {
      return true;
    }
    return parseDateInput(v) !== null;
  });
}

function looksMultiSelect(values: string[]): boolean {
  let multiCount = 0;
  for (const v of values) {
    const parts = parseMultiSelectPlainText(v);
    if (parts.length > 1) {
      multiCount++;
    }
  }
  return multiCount > 0 && multiCount >= Math.max(1, Math.floor(values.length * 0.2));
}

function looksSelect(values: string[]): boolean {
  const unique = new Set(values.map((v) => v.toLowerCase()));
  if (unique.size === 0) {
    return false;
  }
  if (unique.size > 50) {
    return false;
  }
  const ratio = unique.size / values.length;
  const hasRepeats = unique.size < values.length;
  if (values.length < 3 && !hasRepeats) {
    return false;
  }
  return (ratio <= 0.6 && hasRepeats) || unique.size <= 12;
}

export function inferColumnType(values: string[]): DatabaseColumnType {
  const samples = nonEmpty(values);
  if (samples.length === 0) {
    return 'text';
  }
  if (allCheckboxes(samples)) {
    return 'checkbox';
  }
  if (allNumbers(samples)) {
    return 'number';
  }
  if (allUrls(samples)) {
    return 'url';
  }
  if (allDates(samples)) {
    return 'date';
  }
  if (looksMultiSelect(samples)) {
    return 'multi_select';
  }
  if (looksSelect(samples)) {
    return 'select';
  }
  return 'text';
}

export function isImportableColumnType(type: string): type is DatabaseColumnType {
  return IMPORTABLE_TYPES.has(type);
}
